import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { apiCall } from '../config/api';
import {
  Image,
  Database,
  TrendingUp,
  Palette,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Presentation,
  ListChecks,
  LayoutTemplate,
  ShieldAlert,
  Sparkles,
  Plus,
  Pencil,
} from 'lucide-react';
import BrandIcon from './BrandIcon';
import { isFeatureEnabled } from '../config/featureFlags';
import { canAccessAdminPanel } from '../config/auth';
import { fetchCampaignWorkspace, renameCampaignWorkspace } from '../campaign-workspace/api';
import {
  listFormsForSidebar,
  refreshFormForSidebarRename,
  renameFormFromSidebar,
} from '../forms/navigation/formsSidebarApi';

const Sidebar = ({ isCollapsed = true, toggleCollapse, isMobile, onLogout }) => {
  const navigate = useNavigate();
  const currentLocation = useLocation();
  const userName = localStorage.getItem('userName') || 'User';
  const userPicture = localStorage.getItem('userPicture');
  const showAdminPanel = canAccessAdminPanel();

  const [campaigns, setCampaigns] = useState([]);
  const [designSessions, setDesignSessions] = useState([]);
  const [slidesSessions, setSlidesSessions] = useState([]);
  const [forms, setForms] = useState(null);
  const [formsLoadError, setFormsLoadError] = useState(false);
  const [formsReload, setFormsReload] = useState(0);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const [campaignResult, designResult, slidesResult, formsResult] = await Promise.allSettled([
        isFeatureEnabled('campaignWorkspaceMvp') ? apiCall('/api/campaign-workspaces') : Promise.resolve([]),
        apiCall('/api/chat/conversations?limit=12&mode=studio'),
        apiCall('/api/chat/conversations?limit=12&mode=slides'),
        listFormsForSidebar(),
      ]);
      if (!active) return;
      setCampaigns(campaignResult.status === 'fulfilled' && Array.isArray(campaignResult.value) ? campaignResult.value : []);
      setDesignSessions(designResult.status === 'fulfilled' && Array.isArray(designResult.value?.conversations) ? designResult.value.conversations : []);
      setSlidesSessions(slidesResult.status === 'fulfilled' && Array.isArray(slidesResult.value?.conversations) ? slidesResult.value.conversations : []);
      setForms(formsResult.status === 'fulfilled' && Array.isArray(formsResult.value) ? formsResult.value : []);
      setFormsLoadError(formsResult.status === 'rejected');
    };
    void load();
    return () => { active = false; };
  }, [currentLocation.pathname, currentLocation.search, formsReload]);

  const handleLogoClick = () => {
    navigate('/');
    if (isMobile && !isCollapsed) toggleCollapse(); // Close drawer on navigation
  };

  const handleLinkClick = () => {
    if (isMobile && !isCollapsed) toggleCollapse();
  };

  const renameCampaign = async (item, nextTitle) => {
    const result = await renameCampaignWorkspace(
      item.id,
      nextTitle,
      item.version,
    );
    if (result.workspace) {
      setCampaigns((current) => current.map((campaign) => (
        campaign.id === item.id ? { ...campaign, ...result.workspace } : campaign
      )));
    }
    return result;
  };

  const refreshCampaignForRename = async (item) => {
    try {
      const workspace = await fetchCampaignWorkspace(item.id);
      if (workspace) {
        setCampaigns((current) => current.map((campaign) => (
          campaign.id === item.id ? { ...campaign, ...workspace } : campaign
        )));
      }
      return workspace;
    } catch {
      return null;
    }
  };

  const renameForm = async (item, nextTitle) => {
    const result = await renameFormFromSidebar(item, nextTitle);
    if (result.form) {
      setForms((current) => (current || []).map((form) => (
        form.form_id === item.form_id ? result.form : form
      )));
    }
    return result;
  };

  const refreshFormForRename = async (item) => {
    const current = await refreshFormForSidebarRename(item);
    if (current) {
      setForms((formsState) => (formsState || []).map((form) => (
        form.form_id === item.form_id ? current : form
      )));
    }
    return current;
  };

  return (
    <div
      className={`
        bg-[#fbfaf8] h-screen border-r border-[#ece9e4] fixed top-0 left-0 z-30
        transition-all duration-300 ease-in-out flex flex-col
        ${isMobile
          ? (isCollapsed ? '-translate-x-full' : 'translate-x-0 w-64')
          : (isCollapsed ? 'w-[4.5rem]' : 'w-56')}
      `}
    >
      {/* Header / Toggle */}
      <div className={`p-3 flex items-center h-16 border-b border-[#ece9e4] ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
        <div
          onClick={handleLogoClick}
          className="flex items-center gap-2 cursor-pointer group"
        >
          <BrandIcon size={20} />
          {!isCollapsed && (
            <div className="animate-in fade-in duration-200">
              <h1 className="text-xl font-semibold text-gray-900 tracking-tight group-hover:text-violet-700 transition-colors">Niki</h1>
            </div>
          )}
        </div>

        {!isCollapsed && (
          <button
            onClick={toggleCollapse}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
          >
            <ChevronLeft size={20} />
          </button>
        )}
      </div>

      {isCollapsed && (
        <div className="flex justify-center p-2">
          <button
            onClick={toggleCollapse}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Expand sidebar"
            title="Expand sidebar"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      )}

      <nav className="mt-4 px-3 flex-1 overflow-y-auto custom-scrollbar">
        {isCollapsed ? (
          <div className="space-y-1.5">
            {isFeatureEnabled('campaignWorkspaceMvp') && <NavItem to="/campaign-workspaces" icon={<Sparkles size={21} />} label="Niki Agent" isCollapsed onClick={handleLinkClick} />}
            <NavItem to="/studio" icon={<Palette size={21} />} label="Design Agent" isCollapsed onClick={handleLinkClick} />
            <NavItem to="/slides" icon={<Presentation size={21} />} label="Slides Agent" isCollapsed onClick={handleLinkClick} />
            <NavItem to="/forms" icon={<ListChecks size={21} />} label="Forms Agent" isCollapsed onClick={handleLinkClick} />
            <NavItem to="/templates" icon={<LayoutTemplate size={21} />} label="Templates" isCollapsed onClick={handleLinkClick} />
            <NavItem to="/collection" icon={<Image size={21} />} label="Asset Library" isCollapsed onClick={handleLinkClick} />
          </div>
        ) : (
          <div className="space-y-6 pb-4">
            {isFeatureEnabled('campaignWorkspaceMvp') && (
              <SessionGroup
                title="Niki Agent"
                icon={<Sparkles size={15} />}
                items={campaigns}
                itemLabel={(item) => item.title || 'Untitled campaign'}
                itemTo={(item) => `/campaign-workspaces/${item.id}/campaign`}
                emptyLabel="No campaigns yet"
                allTo="/campaign-workspaces"
                createTo="/campaign-workspaces/new"
                entityLabel="campaign"
                onRename={renameCampaign}
                onRefreshRename={refreshCampaignForRename}
                onNavigate={handleLinkClick}
              />
            )}
            <SessionGroup
              title="Design Agent"
              icon={<Palette size={15} />}
              items={designSessions}
              itemLabel={(item) => item.title || 'Untitled design'}
              itemTo={(item) => `/studio?conversationId=${encodeURIComponent(item.conversation_id)}`}
              emptyLabel="No design sessions yet"
              allTo="/campaigns?agent=studio"
              createTo="/studio"
              onNavigate={handleLinkClick}
            />
            <SessionGroup
              title="Forms Agent"
              icon={<ListChecks size={15} />}
              items={forms || []}
              itemKey={(item) => item.form_id}
              itemLabel={(item) => item.title || 'Untitled survey'}
              itemTo={(item) => `/forms/${item.form_id}/edit`}
              emptyLabel="No surveys yet"
              loading={forms === null}
              loadError={formsLoadError ? 'Surveys could not be loaded.' : null}
              onRetryLoad={() => setFormsReload((value) => value + 1)}
              allTo="/forms"
              createTo="/forms/new"
              createLabel="New Survey"
              entityLabel="survey"
              onRename={renameForm}
              onRefreshRename={refreshFormForRename}
              onNavigate={handleLinkClick}
            />
            <SessionGroup
              title="Slides Agent"
              icon={<Presentation size={15} />}
              items={slidesSessions}
              itemLabel={(item) => item.title || 'Untitled presentation'}
              itemTo={(item) => `/slides?conversationId=${encodeURIComponent(item.conversation_id)}`}
              emptyLabel="No presentation sessions yet"
              allTo="/campaigns?agent=slides"
              createTo="/slides"
              onNavigate={handleLinkClick}
            />

            <div className="border-t border-[#ece9e4] pt-5 space-y-1">
              <NavItem to="/templates" icon={<LayoutTemplate size={19} />} label="Templates" isCollapsed={false} onClick={handleLinkClick} quiet />
            </div>

            <div className="border-t border-[#ece9e4] pt-5">
              <p className="mb-2 px-2 text-[11px] font-medium uppercase tracking-[0.13em] text-slate-400">Others</p>
              <div className="space-y-1">
                <NavItem to="/collection" icon={<Image size={19} />} label="Asset Library" isCollapsed={false} onClick={handleLinkClick} quiet />
                <NavItem to="/campaigns" icon={<Database size={19} />} label="Projects" isCollapsed={false} onClick={handleLinkClick} quiet />
              </div>
            </div>
          </div>
        )}

        {showAdminPanel && (
          <div className={`text-xs font-semibold text-gray-400 mt-6 mb-2 px-2 uppercase tracking-wider ${isCollapsed ? 'opacity-0 h-0 hidden' : 'opacity-100'}`}>
            Admin
          </div>
        )}

        {showAdminPanel && (
          <NavItem to="/admin" icon={<ShieldAlert size={22} />} label="Admin Panel" isCollapsed={isCollapsed} onClick={handleLinkClick} />
        )}

        {isFeatureEnabled('analysisHub') && (
          <>
            <div className={`text-xs font-semibold text-gray-400 mt-6 mb-2 px-2 uppercase tracking-wider ${isCollapsed ? 'opacity-0 h-0 hidden' : 'opacity-100'}`}>
              Explore
            </div>
            <NavItem to="/analysis" icon={<TrendingUp size={22} />} label="Analysis Hub" isCollapsed={isCollapsed} onClick={handleLinkClick} />
          </>
        )}
      </nav>

      {/* User Profile (Optional placeholder for bottom) */}
      {/* User Profile / Settings Link */}
      <div className="p-4 border-t border-gray-50">
        <NavLink
          to="/settings"
          className={`flex items-center p-2 rounded-xl transition-colors hover:bg-gray-50 group
            ${isCollapsed ? 'justify-center' : 'gap-3'}`}
          title="User Settings"
          onClick={handleLinkClick}
        >
          {userPicture ? (
            <img src={userPicture} alt={userName} className="w-8 h-8 rounded-full shadow-sm flex-shrink-0 group-hover:shadow-md transition-all object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-purple-500 to-blue-500 shadow-sm flex-shrink-0 group-hover:shadow-md transition-all flex items-center justify-center text-[10px] font-bold text-white">
              {userName[0]}
            </div>
          )}
          {!isCollapsed && (
            <div className="overflow-hidden text-left">
              <p className="text-sm font-black text-gray-700 truncate group-hover:text-violet-600 transition-colors tracking-tight">{userName}</p>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest truncate">Settings</p>
            </div>
          )}
        </NavLink>
      </div>

      {/* Logout Button */}
      <div className="p-4 border-t border-gray-50 mt-auto">
        <button
          type="button"
          onClick={onLogout}
          className={`flex items-center p-2 rounded-xl transition-colors hover:bg-red-50 text-gray-500 hover:text-red-600 group w-full
            ${isCollapsed ? 'justify-center' : 'gap-3'}`}
          title="Log Out"
        >
          <LogOut size={22} className="flex-shrink-0" />
          {!isCollapsed && (
            <span className="text-sm font-medium">Log Out</span>
          )}
        </button>
      </div>
    </div>
  );
};

// Helper Component for Nav Items to keep code clean
const NavItem = ({ to, icon, label, isCollapsed, highlight, onClick, quiet = false }) => (
  <NavLink
    to={to}
    className={({ isActive }) => `
            flex items-center min-h-[40px] rounded-lg transition-all duration-200 group relative
            ${isCollapsed ? 'justify-center px-0' : 'px-2.5'}
            ${isActive
        ? 'bg-violet-100 text-violet-800'
        : highlight
          ? 'bg-violet-100 text-violet-700 shadow-sm hover:bg-violet-200 hover:shadow-md'
          : quiet ? 'text-slate-500 hover:bg-white hover:text-slate-900' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
      }
        `}
    title={isCollapsed ? label : ''}
    onClick={onClick}
  >
    {({ isActive }) => (
      <>
        <span className={`flex-shrink-0 ${highlight && !isActive ? 'text-violet-600' : ''}`}>{icon}</span>
        {!isCollapsed && (
            <span className="ml-3 font-medium whitespace-nowrap overflow-hidden text-sm animate-in fade-in slide-in-from-left-2 duration-300">
            {label}
          </span>
        )}

        {/* Hover Tooltip for Collapsed State */}
        {isCollapsed && (
          <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
            {label}
          </div>
        )}
      </>
    )}
  </NavLink>
);

export default Sidebar;

const SessionGroup = ({ title, icon, items, itemKey = (item) => item.id || item.conversation_id, itemLabel, itemTo, emptyLabel, loading = false, loadError = null, onRetryLoad, allTo, createTo, createLabel, entityLabel = 'session', onRename, onRefreshRename, onNavigate }) => {
  const [showAll, setShowAll] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [renameError, setRenameError] = useState(null);
  const renamePendingRef = useRef(false);
  const submittedRenameDraftRef = useRef(null);
  const location = useLocation();
  const visible = showAll ? items : items.slice(0, 4);

  const beginRename = (item) => {
    setEditingId(itemKey(item));
    setDraftTitle(itemLabel(item));
    setRenameError(null);
    submittedRenameDraftRef.current = null;
  };

  const applyRenameResult = (item, result) => {
    const itemId = itemKey(item);
    if (result?.status === 'updated') {
      setEditingId(null);
      setRenameError(null);
    } else if (result?.status === 'conflict') {
      setRenameError({
        itemId,
        kind: 'conflict',
        message: `This ${entityLabel} changed elsewhere. Review the current name and try again.`,
      });
    } else if (result?.status === 'conflict_refresh_failed') {
      setRenameError({
        itemId,
        kind: 'refresh_required',
        message: `This ${entityLabel} changed elsewhere, but its current version could not be refreshed.`,
      });
    } else {
      setRenameError({
        itemId,
        kind: 'error',
        message: 'Your rename could not be saved. Check your connection and try again.',
      });
    }
  };

  const finishRename = async (item, allowExplicitRetry = false) => {
    if (renamePendingRef.current) return;
    const itemId = itemKey(item);
    if (renameError?.itemId === itemId && renameError.kind === 'refresh_required') return;
    const nextTitle = draftTitle.trim();
    if (!nextTitle || nextTitle === itemLabel(item)) {
      setEditingId(null);
      setRenameError(null);
      return;
    }
    if (!allowExplicitRetry && submittedRenameDraftRef.current === nextTitle) return;
    submittedRenameDraftRef.current = nextTitle;
    renamePendingRef.current = true;
    try {
      const result = await onRename?.(item, nextTitle);
      applyRenameResult(item, result);
    } finally {
      renamePendingRef.current = false;
    }
  };

  const refreshAndRetryRename = async (item) => {
    if (renamePendingRef.current) return;
    const nextTitle = draftTitle.trim();
    if (!nextTitle) return;
    renamePendingRef.current = true;
    try {
      const current = await onRefreshRename?.(item);
      if (!current) {
        setRenameError({
          itemId: itemKey(item),
          kind: 'refresh_required',
          message: `The current ${entityLabel} still could not be refreshed. Check your connection and retry refresh.`,
        });
        return;
      }
      const result = await onRename?.(current, nextTitle);
      applyRenameResult(current, result);
    } finally {
      renamePendingRef.current = false;
    }
  };

  return (
    <section aria-label={title}>
      <div className="mb-1.5 flex min-h-7 items-center justify-between gap-2 px-2 text-[11px] font-semibold uppercase tracking-[0.13em] text-violet-800">
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-violet-700">{icon}</span>
          <span className="truncate">{title}</span>
        </div>
        <Link
          to={createTo}
          onClick={onNavigate}
          aria-label={createLabel || `New ${title} session`}
          title={createLabel || `New ${title} session`}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-white hover:text-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
        >
          <Plus size={14} strokeWidth={2} />
        </Link>
      </div>
      <div className="space-y-0.5">
        {loading && <p role="status" className="px-2.5 py-1.5 text-sm text-slate-400">Loading…</p>}
        {!loading && loadError && (
          <div role="alert" className="px-2.5 py-1.5 text-xs text-rose-600">
            <p>{loadError}</p>
            <button type="button" onClick={onRetryLoad} className="mt-1 font-semibold underline underline-offset-2">Retry</button>
          </div>
        )}
        {!loading && !loadError && visible.map((item) => {
          const itemId = itemKey(item);
          const destination = itemTo(item);
          const isCurrent = destination.startsWith('/forms/')
            ? location.pathname === destination
            : `${location.pathname}${location.search}` === destination;
          const isEditing = editingId === itemId;
          return (
            <div key={itemId} className="group/session relative">
              <div className="flex items-center">
                {isEditing ? (
                  <input
                    aria-label="Session name"
                    autoFocus
                    value={draftTitle}
                    maxLength={256}
                    onChange={(event) => {
                      setDraftTitle(event.target.value);
                      setRenameError((current) => (
                        current?.kind === 'refresh_required' ? current : null
                      ));
                      submittedRenameDraftRef.current = null;
                    }}
                    onBlur={() => void finishRename(item)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') void finishRename(item, true);
                      if (event.key === 'Escape') {
                        setEditingId(null);
                        setRenameError(null);
                      }
                    }}
                    className="min-w-0 flex-1 rounded-md border border-violet-300 bg-white px-2 py-1 text-sm text-slate-800 outline-none ring-2 ring-violet-100"
                  />
                ) : (
                  <Link
                    to={destination}
                    title={itemLabel(item)}
                    aria-current={isCurrent ? 'page' : undefined}
                    onClick={onNavigate}
                    className={`min-w-0 flex-1 truncate rounded-md px-2.5 py-1.5 text-sm transition-colors ${onRename ? 'pr-8' : ''} ${isCurrent ? 'bg-violet-100/70 text-violet-800 font-medium' : 'text-slate-600 hover:bg-white hover:text-slate-900'}`}
                  >
                    {itemLabel(item)}
                  </Link>
                )}
                {onRename && !isEditing && (
                  <button
                    type="button"
                    onClick={() => beginRename(item)}
                    aria-label={`Rename ${itemLabel(item)}`}
                    title="Rename"
                    className="absolute right-1 flex h-6 w-6 items-center justify-center rounded-md text-slate-400 opacity-0 transition-all hover:bg-white hover:text-violet-700 focus:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 group-hover/session:opacity-100"
                  >
                    <Pencil size={12} />
                  </button>
                )}
              </div>
              {isEditing && renameError?.itemId === itemId && (
                <div role="alert" className="px-2 pt-1 text-xs normal-case tracking-normal text-red-600">
                  <p>{renameError.message}</p>
                  {renameError.kind === 'refresh_required' && (
                    <button
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => void refreshAndRetryRename(item)}
                      className="mt-1 font-semibold underline underline-offset-2"
                    >
                      Refresh current {entityLabel}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {!loading && !loadError && items.length === 0 && <p className="px-2.5 py-1.5 text-sm text-slate-400">{emptyLabel}</p>}
      </div>
      <div className="mt-1 px-2.5">
        {items.length > 4 && !showAll ? (
          <button type="button" onClick={() => setShowAll(true)} className="text-xs font-medium text-slate-400 hover:text-violet-700">Show more</button>
        ) : showAll ? (
          <button type="button" onClick={() => setShowAll(false)} className="text-xs font-medium text-slate-400 hover:text-violet-700">Show less</button>
        ) : (
          <NavLink to={allTo} onClick={onNavigate} className="text-xs font-medium text-slate-400 hover:text-violet-700">Show more</NavLink>
        )}
      </div>
    </section>
  );
};
